/**
 * Centralized API configuration for FaceWise frontend
 * Mirrors the backend URL structure from facewise_api/urls.py
 */

import { ENV } from '@/config/environment'

export const API_CONFIG = {
  BASE_URL: ENV.API_BASE_URL,
  VERSIONED_BASE_URL: ENV.API_BASE_URL_V1,
  ENDPOINTS: {
    // Authentication endpoints (from identity_app/urls.py)
    AUTH: {
      LOGIN: '/accounts/login/',
      REGISTER: '/accounts/register/',
      LOGOUT: '/accounts/logout/',
      ME: '/accounts/me/',
      /** Admin-only preview switch between the two editions — not a purchase.
       *  See identity_app.views.me_edition_view. */
      ME_EDITION: '/accounts/me/edition/',
      PROFILE: '/accounts/profile/',
      CHANGE_PASSWORD: '/accounts/change-password/',
      RESET_PASSWORD: '/accounts/reset-password/',
      SEND_RESET_LINK: '/accounts/send-reset-password-link/',
      VERIFY_EMAIL: '/accounts/verify-email/',
      VERIFY_REGISTRATION: '/accounts/verify-registration/',
      REGISTER_EMAIL: '/accounts/register-email/',
      FIRST_LOGIN: '/accounts/first-login/',
    },

    ACCOUNTS: {
      /** What the account's respondent package has left — the counter a
       *  FaceWise customer reads, in people rather than in analysis minutes. */
      PACKAGE: '/account/package/',
      USERS: '/users/',
      USER_DETAIL: (id: string) => `/users/${id}/`,
    },

    /** What we sell, and starting to buy it. The price list itself lives in
     *  `licensing_app/plans.py`; this end never hardcodes a number. */
    BILLING: {
      PLANS: '/billing/plans/',
      CHECKOUT: '/billing/checkout/',
      ACCOUNT: '/billing/account/',
      PORTAL: '/billing/portal/',
      CANCEL_SUBSCRIPTION: '/billing/subscription/cancel/',
      RESUME_SUBSCRIPTION: '/billing/subscription/resume/',
    },

    // Global admin analytics panel (gated by can_view_admin_panel)
    ADMIN: {
      METRICS: '/admin/metrics/',
      GAZE_LAB_MODEL: '/admin/gaze-lab/model/',
    },
    
    // JWT Token endpoints
    TOKEN: {
      OBTAIN: '/token/',
      REFRESH: '/token/refresh/',
    },
    
    TEAM: {
      DETAIL: '/team/',
      USAGE: '/team/usage/',
      INVITATIONS: '/team/invitations/',
      INVITATION_DETAIL: (id: number) => `/team/invitations/${id}/`,
      INVITATION_PREVIEW: '/team/invitations/preview/',
      INVITATION_ACCEPT: '/team/invitations/accept/',
      MEMBER_DETAIL: (userId: number) => `/team/members/${userId}/`,
    },
    PROJECTS: {
      LIST: '/projects/',
      DASHBOARD: '/projects/dashboard/',
      ACTIVITY: '/projects/activity/',
      DETAIL: (id: string) => `/projects/${id}/`,
      EXPORT_ANALYSIS: (id: string | number) => `/projects/${id}/export-analysis/`,
      EMOTION_ANALYSIS: (id: string | number) => `/projects/${id}/emotion-analysis/`,
      EMOTION_SUMMARIES: '/projects/emotion-summaries/',
      INVITE: (id: string | number) => `/projects/${id}/invite/`,
    },

    // Researcher respondent address book
    RESPONDENTS: {
      LIST: '/respondents/',
      DETAIL: (id: number) => `/respondents/${id}/`,
      BULK: '/respondents/bulk/',
      TAGS: '/respondent-tags/',
    },

    // Email invitation endpoints
    INVITATIONS: {
      SEND: '/accounts/send-invitation/',
    },
    
    // Core API resources (from api_app/urls.py)
    PATIENTS: {
      LIST: '/patients/',
      DETAIL: (id: string) => `/patients/${id}/`,
      ASSESSMENTS: (id: string) => `/patients/${id}/assessments/`,
      ASSESSMENT_TOKENS: (id: string, assessmentId: string) => 
        `/patients/${id}/assessments/${assessmentId}/tokens/`,
    },
    
    ASSESSMENTS: {
      LIST: '/assessments/',
      DETAIL: (id: string) => `/assessments/${id}/`,
      EXPORT_CSV: '/assessments/export-csv/',
      REPORT: (id: string, reportType: string) => 
        `/assessments/${id}/report/${reportType}/`,
    },
    
    QUESTIONNAIRE_TEMPLATES: {
      LIST: '/study-content/questionnaire-templates/',
      DETAIL: (id: number) => `/study-content/questionnaire-templates/${id}/`,
    },
    
    ANSWERS: {
      LIST: '/answers/',
      DETAIL: (id: number) => `/answers/${id}/`,
    },
    
    // Therapist-specific endpoints
    THERAPIST: {
      ASSESSMENTS: {
        LIST: '/therapist/assessments/',
        DETAIL: (id: string) => `/therapist/assessments/${id}/`,
        REACTION: (id: string) => `/therapist/assessments/${id}/reaction/`,
      },
    },
    
    // Patient-specific endpoints (for public access)
    PATIENT: {
      ASSESSMENT: (id: string, token: string) => 
        `/patient/assessments/${id}/${token}/`,
    },
  },
  
  // HTTP methods
  METHODS: {
    GET: 'GET',
    POST: 'POST',
    PUT: 'PUT',
    PATCH: 'PATCH',
    DELETE: 'DELETE',
  },
  
  // Default headers
  DEFAULT_HEADERS: {
    'Content-Type': 'application/json',
  },
} as const

// Type for API configuration
export type ApiConfig = typeof API_CONFIG
